<script>
// Sidebar estudiante — Estilo APPMISIONATE
import MainMenuComponent from '../features/MainMenuComponent';
import SessionBoxView from './SessionBoxView.vue';
import NotificationAlertsView from './NotificationAlertsView.vue';
import BrandLogo from '../BrandLogo.vue';

export default {
  name: 'MainMenuStudentView',
  components: {
    'session-box-view': SessionBoxView,
    'notification-alerts-view': NotificationAlertsView,
    'brand-logo': BrandLogo,
  },
  mixins: [MainMenuComponent],
  data() {
    return {
      isCollapsed: false,
      isMobileOpen: false,
      // Total de notificaciones nuevas; lo publica NotificationAlertsView
      // (única instancia y única conexión SignalR) para pintarlo junto al texto.
      totalNotifications: 0,
    };
  },
  methods: {
    onTotalNotificationsChange(total) {
      this.totalNotifications = Number(total) || 0;
    },
    onToggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      const sidebar = document.getElementById('sidebarAp');
      const wrappers = document.querySelectorAll('.wrapper');
      if (sidebar) {
        sidebar.classList.toggle('collapsed', this.isCollapsed);
      }
      wrappers.forEach(w => w.classList.toggle('sidebar-collapsed', this.isCollapsed));
    },
    onToggleMobile() {
      this.isMobileOpen = !this.isMobileOpen;
      const sidebar = document.getElementById('sidebarAp');
      if (sidebar) {
        sidebar.classList.toggle('mobile-open', this.isMobileOpen);
      }
    },
    onCloseMobile() {
      this.isMobileOpen = false;
      const sidebar = document.getElementById('sidebarAp');
      if (sidebar) {
        sidebar.classList.remove('mobile-open');
      }
    },
    onNavigationClick() {
      if (window.innerWidth <= 1024) {
        this.onCloseMobile();
      }
    },
  },
};
</script>

<template>
  <div>
    <!-- Botón flotante para móvil (<= 1024px) -->
    <button
      type="button"
      class="sidebar-mobile-floating-btn"
      title="Abrir menú"
      aria-label="Abrir menú"
      @click="onToggleMobile"
    >
      <i class="fas fa-bars" />
    </button>

    <!-- Overlay backdrop en móvil -->
    <div
      v-if="isMobileOpen"
      class="sidebar-mobile-backdrop"
      @click="onCloseMobile"
    />

    <!-- Left Sidebar — estilo APPMISIONATE -->
    <nav
      id="sidebarAp"
      class="sidebar-ap"
    >
      <!-- Cabecera del Sidebar: Brand (con navegación a Home) + Toggle Menu -->
      <div class="sidebar-header">
        <router-link
          to="/estudiante/resumen"
          class="sidebar-brand-link"
          title="Appmisiónate - Inicio"
          @click="onNavigationClick"
        >
          <brand-logo text-class="sidebar-brand-text" />
        </router-link>

        <div class="sidebar-header-actions">
          <button
            type="button"
            class="sidebar-toggle-btn"
            title="Colapsar / Expandir menú"
            aria-label="Colapsar / Expandir menú"
            @click="onToggleSidebar"
          >
            <i
              class="fas"
              :class="isCollapsed ? 'fa-chevron-right' : 'fa-bars'"
            />
          </button>
        </div>
      </div>

      <!-- Navegación principal -->
      <div class="sidebar-nav">
        <router-link
          v-if="onCanShowModule('Dashboard-Module')"
          to="/estudiante/resumen"
          class="sidebar-link"
          active-class="active"
          title="Resumen"
          @click="onNavigationClick"
        >
          <i class="fas fa-th-large menu--icon" />
          <span class="menu--label">Resumen</span>
        </router-link>

        <router-link
          v-if="onCanShowModule('LiveClassContent-Module')"
          to="/estudiante/en-vivo"
          class="sidebar-link"
          active-class="active"
          title="Clases en vivo"
          @click="onNavigationClick"
        >
          <i class="fas fa-broadcast-tower menu--icon" />
          <span class="menu--label">
            En Vivo
            <span class="menu--live-badge">LIVE</span>
          </span>
        </router-link>

        <router-link
          v-if="onCanShowModule('CourseContent-Module')"
          to="/estudiante/cursos"
          class="sidebar-link"
          active-class="active"
          title="Cursos"
          @click="onNavigationClick"
        >
          <i class="fas fa-book-open menu--icon" />
          <span class="menu--label">Cursos</span>
        </router-link>

        <router-link
          v-if="onCanShowModule('SimulatorsContent-Module')"
          to="/estudiante/simuladores"
          class="sidebar-link"
          active-class="active"
          title="Simuladores"
          @click="onNavigationClick"
        >
          <i class="fas fa-laptop-code menu--icon" />
          <span class="menu--label">Simuladores</span>
        </router-link>

        <div class="sidebar-divider" />

        <router-link
          v-if="onCanShowModule('NotificationsContent-Notifications')"
          to="/estudiante/notificaciones"
          class="sidebar-link"
          active-class="active"
          title="Notificaciones"
          @click="onNavigationClick"
        >
          <notification-alerts-view
            variant="sidebar"
            :collapsed="isCollapsed"
            @total-change="onTotalNotificationsChange"
          />
          <span class="menu--label">
            Notificaciones
            <span
              v-if="totalNotifications > 0 && !isCollapsed"
              class="menu--notif-badge"
              :title="`${totalNotifications} notificación(es) nueva(s)`"
            >{{ totalNotifications > 99 ? '99+' : totalNotifications }}</span>
          </span>
        </router-link>

        <router-link
          to="/estudiante/mi-cuenta"
          class="sidebar-link"
          active-class="active"
          title="Mi Cuenta"
          @click="onNavigationClick"
        >
          <i class="fas fa-user-circle menu--icon" />
          <span class="menu--label">Mi Cuenta</span>
        </router-link>
      </div>

      <!-- Footer del sidebar con SessionBox exclusivo -->
      <div class="sidebar-footer">
        <session-box-view />
      </div>
    </nav>
  </div>
</template>

<style scoped>
/* Header del Sidebar */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  min-height: 64px;
  border-bottom: 1px solid var(--sb-border, rgba(255, 255, 255, 0.08));
  flex-shrink: 0;
}

.sidebar-brand-link {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none !important;
  color: inherit;
  min-width: 0;
  flex: 1;
}

/* La tipografía del wordmark la define <BrandLogo /> (fuente única de la marca).
   Aquí solo queda la regla de colapso del sidebar. */

.sidebar-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.sidebar-toggle-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.sidebar-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.25);
}

/* La campana de notificaciones vive dentro de la fila "Notificaciones" del menú;
   sus estilos viven en NotificationAlertsView.vue (variante `sidebar`). */
:deep(.sidebar-link .notification-alerts-container--sidebar) {
  flex-shrink: 0;
}

/* Footer del Sidebar con SessionBox */
.sidebar-footer {
  margin-top: auto;
  padding: 12px 14px;
  border-top: 1px solid var(--sb-border, rgba(255, 255, 255, 0.08));
  background: rgba(255, 255, 255, 0.02);
  flex-shrink: 0;
}

:deep(.sidebar-footer .session-box-container) {
  width: 100%;
}

:deep(.sidebar-footer .el-dropdown) {
  width: 100%;
}

:deep(.sidebar-footer .session-trigger) {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

:deep(.sidebar-footer .session-trigger:hover) {
  background: rgba(255, 255, 255, 0.08) !important;
}

:deep(.sidebar-footer .session-name) {
  color: #f8fafc !important;
  font-size: 0.85rem !important;
  font-weight: 700 !important;
  line-height: 1.25;
}

:deep(.sidebar-footer .session-role) {
  color: #94a3b8 !important;
  font-size: 0.72rem !important;
  line-height: 1.2;
}

:deep(.sidebar-footer .session-arrow) {
  color: #64748b !important;
  font-size: 0.85rem !important;
  margin-left: auto;
}

:deep(.sidebar-footer .session-avatar) {
  border-color: rgba(255, 255, 255, 0.2) !important;
  flex-shrink: 0;
}

/* Estado Colapsado (#sidebarAp.collapsed) */
:deep(#sidebarAp.collapsed) {
  width: 64px !important;
}

:deep(#sidebarAp.collapsed .sidebar-header) {
  flex-direction: column;
  padding: 12px 6px;
  gap: 8px;
  justify-content: center;
}

:deep(#sidebarAp.collapsed .sidebar-brand-text) {
  display: none !important;
}

:deep(#sidebarAp.collapsed .sidebar-brand-link) {
  justify-content: center;
}

:deep(#sidebarAp.collapsed .sidebar-header-actions) {
  flex-direction: column;
  gap: 6px;
}

:deep(#sidebarAp.collapsed .sidebar-toggle-btn) {
  width: 32px;
  height: 32px;
}

:deep(#sidebarAp.collapsed .sidebar-section-label) {
  display: none !important;
}

:deep(#sidebarAp.collapsed .menu--label) {
  display: none !important;
}

:deep(#sidebarAp.collapsed .sidebar-link) {
  justify-content: center;
  padding: 0;
  margin: 0 6px;
}

:deep(#sidebarAp.collapsed .sidebar-footer) {
  padding: 10px 4px !important;
  display: flex !important;
  justify-content: center !important;
}

:deep(#sidebarAp.collapsed .sidebar-footer .session-trigger) {
  justify-content: center !important;
  padding: 4px !important;
}

:deep(#sidebarAp.collapsed .sidebar-footer .session-info) {
  display: none !important;
}

:deep(#sidebarAp.collapsed .sidebar-footer .session-arrow) {
  display: none !important;
}

/* Botón flotante móvil (<= 1024px) */
.sidebar-mobile-floating-btn {
  display: none;
  position: fixed;
  top: 10px;
  left: 12px;
  z-index: 850;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #0f172a;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.35);
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.sidebar-mobile-floating-btn:hover {
  background: #1e293b;
  transform: scale(1.05);
}

.sidebar-mobile-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(7, 18, 40, 0.55);
  backdrop-filter: blur(4px);
  z-index: 890;
}

@media (max-width: 1024px) {
  .sidebar-mobile-floating-btn {
    display: flex;
  }
}
</style>
